import { TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
/** keep for dev purposes */
// import { CSSResultGroup } from 'lit';
// import componentStyles from './se-main-nav.css';
import { SeMainHeader } from '../se-main-header/se-main-header';
import { MobileController } from '../../controllers/mobile-controller';

/**
 * The SeMainNav component
 * @element se-main-nav
 */
@customElement('se-main-nav')
export class SeMainNav extends OutlineElement {
  /** keep for dev purposes */
  // static styles: CSSResultGroup = [componentStyles];
  mobileController = new MobileController(this);

  /**
   * Exposes full component to light-DOM
   */
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  nav: HTMLElement;

  @property({ type: Object })
  mainMenu: HTMLElement;

  @property({ type: Array })
  topLevelLinks: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelItems: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelLists: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelDropdowns: Array<HTMLElement> = [];

  @property({ type: Array })
  subLevelLinks: Array<HTMLElement> = [];

  @property({ type: Boolean })
  isMobile: boolean;

  seed = Math.floor(Math.random() * 1000000).toString();

  render(): TemplateResult {
    return html``;
  }

  firstUpdated() {
    this.setTopLevelPropsIDs();
    this.setClassNames();
    this.setIsMobile();
    this.handleButtons();
    this.addEventListeners();
    this.handleSafariOnLoad();
    window.addEventListener('resize', () => this.setDropdownWidth);
  }

  updated() {
    this.setDropdownWidth();
  }

  setTopLevelPropsIDs() {
    this.nav = this.querySelector('nav') as HTMLElement;
    this.mainMenu = [...this.nav?.children].filter(
      child => child.localName === 'ul'
    )[0] as HTMLElement;

    [...this.mainMenu?.children]
      .filter(child => child.localName === 'li')
      .forEach((li, i) => {
        li.setAttribute('id', `item--${this.seed}-${i}`);
        this.topLevelItems.push(li as HTMLElement);
        const mainElements = [...li.children] as HTMLElement[];

        mainElements.forEach(el => {
          if (el.localName === 'button') {
            el.setAttribute('id', `button--${this.seed}-${i}`);
            this.topLevelDropdowns.push(el);
          }
          if (el.localName === 'ul') {
            el.setAttribute('id', `sub--${this.seed}-${i}`);
            this.topLevelLists.push(el);
          }
          if (el.localName === 'a') {
            el.setAttribute('id', `link--${this.seed}-${i}`);
            this.topLevelLinks.push(el);
          }
        });
      });
  }

  setClassNames() {
    this.nav.classList.add('se-main-nav--nav');
    this.mainMenu.classList.add('se-main-nav--menu');
    this.topLevelLinks.forEach(a => a.classList.add('se-main-nav--link'));
    this.topLevelItems.forEach(li => li.classList.add('se-main-nav--item'));
    this.topLevelDropdowns.forEach(button =>
      button.classList.add('se-main-nav--button')
    );
    this.topLevelLists.forEach(ul => ul.classList.add('se-main-nav--sub-menu'));

    this.topLevelLists.forEach(ul => {
      const list = ul as HTMLUListElement;
      [...list.children].forEach(li => {
        const subLink = li.children[0] as HTMLElement;
        subLink.classList.add('se-main-nav--sub-link');
        this.subLevelLinks.push(subLink);
      });
    });
  }

  addEventListeners() {
    const parentHeader = document.querySelector(
      'se-main-header'
    ) as SeMainHeader;
    parentHeader.addEventListener('keydown', parentHeader.handleEscape);

    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('mouseover', parentHeader.applyOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('mouseout', parentHeader.removeOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('blur', parentHeader.removeOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('keydown', parentHeader.handleOverlayOnKeydown)
    );
  }

  handleButtons() {
    this.topLevelDropdowns.forEach(button => {
      const el = button as HTMLElement;
      const listID = 'sub--'.concat(el.id.split('--')[1]);
      const replacement = document.createElement('se-dropdown');
      this.topLevelDropdowns.push(replacement);

      const list = this.topLevelLists.filter(el => el.id === listID)[0];
      list.setAttribute('slot', 'dropdown');
      replacement.appendChild(list);
      replacement.setAttribute('trigger-text', el.title);
      replacement.setAttribute('trigger-label', `${el.title} menu`);
      replacement.setAttribute('id', el.id);

      el.insertAdjacentElement('beforebegin', replacement);
      el.remove();
    });

    //cleanup
    this.topLevelDropdowns = this.topLevelDropdowns.filter(
      el => el.localName === 'se-dropdown'
    );
  }

  /**
   * positions dropdown panel on desktop.
   */
  setDropdownWidth() {
    const header = document.querySelector('se-main-header') as HTMLElement;

    const menu = document.querySelector('.se-main-nav--menu') as HTMLElement;

    const searchElement = header.shadowRoot?.querySelector(
      '.se-main-header--search'
    ) as HTMLElement;

    // ref to this cmponent to allow window resize listener to work.
    const thisComponent = document.querySelector('se-main-nav') as SeMainNav;

    thisComponent.topLevelDropdowns.map(el => {
      const dropdownPanel = el?.shadowRoot?.querySelector(
        '.dropdown__panel'
      ) as HTMLElement;

      const dropdownSlotWrapper = el.shadowRoot?.querySelector(
        '.control-spacer'
      ) as HTMLElement;

      setTimeout(() => {
        if (dropdownPanel) {
          const panelPostionRight =
            el.getBoundingClientRect().right -
            searchElement.getBoundingClientRect().right;

          const panelPostionTop = el.getBoundingClientRect().height;

          const searchElementPositionTop =
            header.getBoundingClientRect().height -
            menu.getBoundingClientRect().top;

          if (this.mobileController.isMobile) {
            dropdownPanel.style.right = `0px`;
            dropdownSlotWrapper.style.top = `0px`;
          } else {
            dropdownPanel.style.right = `${panelPostionRight}px`;
            dropdownPanel.style.top = `${panelPostionTop}px`;
            dropdownSlotWrapper.style.top = `${
              searchElementPositionTop - panelPostionTop
            }px`;
          }
        }
      }, 200);
    });
  }

  /**
   * If browser is safari, fires setDropdownWidth 300ms after load because safari.
   */
  handleSafariOnLoad() {
    const userAgent = navigator.userAgent;
    const isSafari =
      userAgent.includes('Safari') && !userAgent.includes('Chrome');
    if (isSafari) {
      setTimeout(() => {
        this.setDropdownWidth();
      }, 300);
    }
  }

  setIsMobile() {
    this.isMobile = this.mobileController.isMobile;
  }
}
